import {
  OnboardingLiveCreationIntentItemFragment,
  OnboardingLiveCreationIntentStatus,
} from "../../gql/graphql.ts";
import { matchesRange, NumberRange } from "../../UI/Table/numberRange.ts";
import { DateRange, matchesDateRange } from "../../UI/Table/dateRange.ts";
import { matchesSelection } from "../../UI/Table/matchesSelection.ts";
import {
  parseDateRangeParams,
  parseKnownListParam,
  parseListParam,
  parseRangeParams,
  setDateRangeParams,
  setOrDeleteParam,
  setRangeParams,
  TableSort,
} from "../../UI/Table/listUrlParams.ts";
import { onboardingLiveTimezone } from "../onboardingLiveTime.ts";

export const ONBOARDING_LIVE_PLANS_SORT_FIELDS = [
  "createdAt",
  "createdBy",
  "userGroup",
  "slots",
  "status",
] as const;

export type OnboardingLivePlansSortField =
  (typeof ONBOARDING_LIVE_PLANS_SORT_FIELDS)[number];

export type OnboardingLivePlansSort = TableSort<OnboardingLivePlansSortField>;

export const ONBOARDING_LIVE_PLANS_DEFAULT_SORT: OnboardingLivePlansSort = {
  sortBy: "createdAt",
  sortDirection: "desc",
};

export const CREATION_INTENT_STATUSES_IN_LIFECYCLE_ORDER: OnboardingLiveCreationIntentStatus[] =
  [
    OnboardingLiveCreationIntentStatus.Draft,
    OnboardingLiveCreationIntentStatus.Confirmed,
    OnboardingLiveCreationIntentStatus.Completed,
    OnboardingLiveCreationIntentStatus.Cancelled,
  ];

export function creationIntentStatusRank(
  status: OnboardingLiveCreationIntentStatus,
): number {
  return CREATION_INTENT_STATUSES_IN_LIFECYCLE_ORDER.indexOf(status);
}

export type OnboardingLivePlansFilters = {
  createdAt: DateRange;
  creatorEmails: string[];
  userGroupIds: string[];
  slots: NumberRange;
  statuses: OnboardingLiveCreationIntentStatus[];
};

export function parseOnboardingLivePlansFilters(
  params: URLSearchParams,
): OnboardingLivePlansFilters {
  return {
    createdAt: parseDateRangeParams(params),
    creatorEmails: parseListParam(params.get("creators")),
    userGroupIds: parseListParam(params.get("groups")),
    slots: parseRangeParams(params, "slots"),
    statuses: parseKnownListParam(
      params.get("statuses"),
      CREATION_INTENT_STATUSES_IN_LIFECYCLE_ORDER,
    ),
  };
}

export function writeOnboardingLivePlansFilters(
  params: URLSearchParams,
  filters: OnboardingLivePlansFilters,
): void {
  setDateRangeParams(params, filters.createdAt);
  setOrDeleteParam(params, "creators", filters.creatorEmails.join(","));
  setOrDeleteParam(params, "groups", filters.userGroupIds.join(","));
  setRangeParams(params, "slots", filters.slots);
  setOrDeleteParam(params, "statuses", filters.statuses.join(","));
}

export function matchesOnboardingLivePlansFilters(
  intent: OnboardingLiveCreationIntentItemFragment,
  filters: OnboardingLivePlansFilters,
): boolean {
  return (
    matchesDateRange(
      intent.createdAtUtc,
      filters.createdAt,
      onboardingLiveTimezone,
    ) &&
    matchesSelection(intent.createdByEmail ?? "", filters.creatorEmails) &&
    matchesSelection(intent.userGroupId, filters.userGroupIds) &&
    matchesRange(intent.slots.length, filters.slots) &&
    matchesSelection(intent.status, filters.statuses)
  );
}
